import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Shield, ArrowLeft, RefreshCw, Stethoscope } from 'lucide-react';
import { verifyOtp, forgotPassword } from '../../api/auth';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { toast } from 'react-toastify';

export default function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState('');
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Get email from navigation state or redirect if missing
  useEffect(() => {
    const stateEmail = location.state?.email;
    if (stateEmail) {
      setEmail(stateEmail);
      setValue('email', stateEmail);
    } else {
      // If no email in state, redirect to forgot password
      toast.error('Please start with forgot password process');
      navigate('/forgot');
    }
  }, [location.state, setValue, navigate]);

  // Timer for resend OTP
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const res = await verifyOtp({ email: data.email, otp: data.otp });
      toast.success('OTP verified successfully!');
      
      // Navigate to reset password with email and otp
      navigate('/reset-password', { 
        state: { 
          email: data.email, 
          otp: data.otp,
          fromVerifyOtp: true 
        }
      });
    } catch (error) {
      toast.error(error.message || 'Invalid or expired OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!canResend || !email) return;
    
    setIsResending(true);
    try {
      await forgotPassword({ email });
      toast.success('New OTP sent to your email!');
      setTimer(60);
      setCanResend(false);
    } catch (error) {
      toast.error(error.message || 'Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <div className="bg-primary rounded-full p-3">
              <Stethoscope className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">SmartCare HMS</h1>
          <p className="text-muted-foreground">Enter the verification code</p>
        </div>

        {/* Verify OTP Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="flex justify-center items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-foreground">Verify OTP</h2>
            <p className="text-muted-foreground mt-1">
              Enter the 6-digit code sent to
            </p>
            <p className="text-foreground font-medium">{email}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Input
                label="Email Address"
                type="email"
                value={email}
                disabled
                className="bg-muted"
                {...register('email', { required: 'Email is required' })}
              />
            </div>

            <div>
              <Input
                label="OTP Code"
                type="text"
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                {...register('otp', { 
                  required: 'OTP is required',
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: 'OTP must be exactly 6 digits'
                  }
                })}
                error={errors.otp?.message}
                className="text-center text-lg tracking-wider"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verifying...
                </div>
              ) : (
                'Verify OTP'
              )}
            </Button>
          </form>

          {/* Resend OTP */}
          <div className="text-center mt-6">
            {canResend ? (
              <button
                onClick={resendOtp}
                disabled={isResending}
                className="text-primary hover:text-primary/80 font-medium text-sm flex items-center justify-center mx-auto"
              >
                {isResending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                    Resending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Resend OTP
                  </>
                )}
              </button>
            ) : (
              <p className="text-muted-foreground text-sm">
                Resend OTP in {timer} seconds
              </p>
            )}
          </div>

          {/* Back to Forgot Password */}
          <div className="text-center mt-4">
            <Link
              to="/forgot"
              className="text-muted-foreground hover:text-foreground text-sm flex items-center justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Forgot Password
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground">
            © 2024 SmartCare HMS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
